/**
 * Shared employee document types for API contracts between frontend and backend
 *
 * EmployeeDocument links Document records to Employment records with metadata.
 * This creates employee-specific "pointers" to company-level documents,
 * with optional skill linking and folder categorization via tags.
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ===== CONSTANTS =====
/**
 * Fixed folder types for employee documents (Prisma enum)
 */
export const EmployeeDocumentFolderTypeSchema = z.enum([
    "COMPANY",
    "LEGAL",
    "SKILL_RELATED",
    "PERFORMANCE",
]);
// ===== REQUEST SCHEMAS =====
/**
 * POST /employments/:employmentId/documents
 * Attach a document to an employee
 */
export const AttachEmployeeDocumentInputSchema = z.object({
    documentId: uuidSchema,
    employeeSkillId: uuidSchema.optional(), // Link to employee's skill
    folderType: EmployeeDocumentFolderTypeSchema, // Folder type (required)
    expirationDate: z.string().datetime().optional(), // Employee-specific override
    notes: z.string().max(5000).optional(),
    requiresAcknowledgement: z.boolean().default(false),
});
/**
 * PATCH /employments/:employmentId/documents/:employeeDocumentId
 * Update employee document metadata
 */
export const UpdateEmployeeDocumentInputSchema = z.object({
    employeeSkillId: uuidSchema.nullable().optional(),
    folderType: EmployeeDocumentFolderTypeSchema.optional(),
    expirationDate: z.string().datetime().nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    requiresAcknowledgement: z.boolean().optional(),
});
// ===== QUERY SCHEMAS =====
/**
 * GET /employments/:employmentId/documents query params
 */
export const ListEmployeeDocumentsQuerySchema = z.object({
    folderType: EmployeeDocumentFolderTypeSchema.optional(), // Filter by folder type
    employeeSkillId: uuidSchema.optional(), // Filter by skill
    acknowledged: z.coerce.boolean().optional(), // Filter by acknowledgement status
    includeExpired: z.coerce.boolean().optional(), // Include expired documents
});
// ===== PARAMETER SCHEMAS =====
/**
 * Route params for employee document operations
 */
export const EmployeeDocumentParamsSchema = z.object({
    employmentId: uuidSchema,
    employeeDocumentId: uuidSchema,
});
/**
 * Route params for employment-only operations (list, attach)
 */
export const EmploymentIdParamsSchema = z.object({
    employmentId: uuidSchema,
});
// ===== RESPONSE SCHEMAS =====
/**
 * File info embedded in document response
 */
export const EmbeddedFileInfoSchema = z.object({
    id: z.uuid(),
    fileName: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
    uploadedAt: z.iso.datetime(),
});
/**
 * Document info (the company-level document being linked)
 */
export const DocumentInfoSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    signatureRequired: z.boolean(),
    expirationDate: z.iso.datetime().nullable(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
    file: EmbeddedFileInfoSchema,
});
/**
 * Base skill info (the skill definition)
 */
export const BaseSkillInfoSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
});
/**
 * Employee skill info (the employee's specific skill record)
 */
export const EmployeeSkillInfoSchema = z.object({
    id: z.uuid(),
    expirationDate: z.iso.datetime().nullable(),
    skillVerified: z.boolean(),
    baseSkill: BaseSkillInfoSchema,
});
/**
 * Single employee document response
 */
export const EmployeeDocumentResponseSchema = z.object({
    id: z.uuid(),
    employmentId: z.uuid(),
    documentId: z.uuid(),
    employeeSkillId: z.uuid().nullable(),
    folderType: EmployeeDocumentFolderTypeSchema,
    expirationDate: z.iso.datetime().nullable(),
    notes: z.string().nullable(),
    requiresAcknowledgement: z.boolean(),
    acknowledged: z.boolean(),
    acknowledgedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    // Nested info
    document: DocumentInfoSchema,
    employeeSkill: EmployeeSkillInfoSchema.nullable(),
});
/**
 * List of employee documents
 */
export const EmployeeDocumentListResponseSchema = z.object({
    documents: z.array(EmployeeDocumentResponseSchema),
});
